import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
	PAYMENT_FREQUENCY, PaymentFrequency, TimecardBuilderService,
	TimecardPeriod,
	VIEW_DATE_FORMAT
} from '../timecards-builder.service';

import {CookieService} from 'ngx-cookie';
import {TimecardsCookieState} from '../timecards/timecards.component';
import {
	EmployeeEditHistoryDetails
} from '../../core/employee-edit-history-cache.service';
import {isEqual, find, filter, each} from 'lodash';

import * as moment from 'moment';
import {Moment} from 'moment';
import {UserService} from '../../shared/user.service';


@Component({
	selector: 'tt-period-selector',
	templateUrl: 'period-selector.component.html',
	styleUrls: ['period-selector.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeriodPickerComponent implements OnInit, OnDestroy {
	@Input('admin') admin: boolean;
	@Input('stateCookieName') stateCookieName: string;
	@Input('userDetails') userDetails: EmployeeEditHistoryDetails;
	@Output('periodSelected') periodSelected: EventEmitter<TimecardPeriod> = new EventEmitter();
	@Output('filterInited') filterInited: EventEmitter<void> = new EventEmitter<void>();

	public selectedYear: number;
	public selectedPeriod: TimecardPeriod;
	public periods: TimecardPeriod[];
	public selectedPaymentFrequency: PaymentFrequency;
	public years: number[];
	private currentYear: number;
	private sequenceIds: {[period: string]: string} = {};

	constructor(
		private timecardBuilderService: TimecardBuilderService,
		private cookieService: CookieService,
		private userService: UserService
	) {
		this.years = this.timecardBuilderService.getYears(moment().year());
	}

	ngOnInit(): void {
		this.currentYear = moment().year();
		let periodInited: boolean = false;
		let paymentFrequencyInited: boolean = false;

		if (this.stateCookieName) {
			if (this.cookieService.get(this.stateCookieName)) {
				const state: TimecardsCookieState = this.cookieService.getObject(this.stateCookieName) as TimecardsCookieState;

				if (state && state.paymentFrequency) {
					this.selectedPaymentFrequency = state.paymentFrequency;
					paymentFrequencyInited = true;
				} else {
					this.selectedPaymentFrequency = this.userDetails.payFrequency as PaymentFrequency || PAYMENT_FREQUENCY.BIWEEKLY;
					paymentFrequencyInited = true;
				}

				if (state && state.startDate && state.endDate) {
					this.selectYear(moment(state.startDate, VIEW_DATE_FORMAT).year());
					this.selectedPeriod = [state.startDate, state.endDate];
					periodInited = true;
				}
			}
		}

		if (!paymentFrequencyInited) {
			this.selectedPaymentFrequency = this.userDetails.payFrequency as PaymentFrequency || PAYMENT_FREQUENCY.BIWEEKLY;
			if (this.selectedPaymentFrequency === PAYMENT_FREQUENCY.NO_TIMECARD) {
				this.selectedPaymentFrequency = PAYMENT_FREQUENCY.BIWEEKLY;
			}
		}

		if (!periodInited) {
			const todayPeriod: TimecardPeriod = this.getTodayPeriod(this.selectedPaymentFrequency);
			if (todayPeriod) {
				this.selectedPeriod = [todayPeriod[0], todayPeriod[1]];
				this.selectYear(moment(todayPeriod[0], VIEW_DATE_FORMAT).year());
			}
		}

		this.filterInited.emit();
	}

	ngOnDestroy(): void {

	}

	public getTodayPeriod(paymentFrequency: PaymentFrequency): TimecardPeriod {
		const today: Moment = moment().startOf('day');
		const currentYear: number = today.year();
		const periods: TimecardPeriod[] = this.timecardBuilderService.getYearPeriods(paymentFrequency, currentYear);
		if (periods && periods.length > 0) {
			return find(periods, (period: TimecardPeriod) => {
				return today.isBetween(moment(period[0], VIEW_DATE_FORMAT), moment(period[1], VIEW_DATE_FORMAT), null, '[]');
			});
		}

		return null;
	}

	public changePaymentFrequency(value: PaymentFrequency): void {
		this.selectedPaymentFrequency = value;
		if (this.currentYear === this.selectedYear) {
			this.periods = this.removeFuturePeriods(this.timecardBuilderService.getYearPeriods(this.selectedPaymentFrequency, this.selectedYear));
		} else {
			this.periods = this.timecardBuilderService.getYearPeriods(this.selectedPaymentFrequency, this.selectedYear);
		}
	}

	public selectYear(year: number): void {
		this.selectedYear = year;
		if (this.selectedYear === this.currentYear) {
			this.periods = this.removeFuturePeriods(this.timecardBuilderService.getYearPeriods(this.selectedPaymentFrequency, this.selectedYear));
		} else {
			this.periods = this.timecardBuilderService.getYearPeriods(this.selectedPaymentFrequency, this.selectedYear);
		}

		this.sequenceIds = this.calculatePeriodsSequenceIds(this.periods);
	};

	public selectPeriod(period: TimecardPeriod): void {
		if (!this.admin && !this.isPeriodContainsTimecard(period)) { return; }
		this.selectedPeriod = period;
		this.periodSelected.emit(period);
	}


	public getPeriodSequenceId(period: TimecardPeriod): string {
		return this.sequenceIds[`${period[0]}-${period[1]}`];
	}

	public isPeriodContainsTimecard(period: TimecardPeriod): boolean {
		return !!this.getPeriodSequenceId(period);
	}


	public calculateTimecardSequenceId(period: TimecardPeriod): string {
		return this.timecardBuilderService.getTimecardSequenceId(this.userService.user, period);

	}

	public isSelectedPeriod(period: TimecardPeriod): boolean {
		return isEqual(this.selectedPeriod, period);
	}

	public getSelectedPeriod(): TimecardPeriod {
		return this.selectedPeriod;
	}

	public getSelectedPaymentFrequency(): PaymentFrequency {
		return this.selectedPaymentFrequency;
	}

	public formatPeriodName(range: string[]): string {
		const sDate: Moment = moment(range[0], VIEW_DATE_FORMAT);
		const fDate: Moment = moment(range[1], VIEW_DATE_FORMAT);
		return `${sDate.format('YYYY MM-DD')} to ${fDate.format('MM-DD')}`;
	};

	private calculatePeriodsSequenceIds(periods: TimecardPeriod[]): {[period: string]: string} {
		const result: any = {};
		let sequenceId: string = null;

		each(periods, (period: TimecardPeriod) => {
			sequenceId = this.timecardBuilderService.getTimecardSequenceId(this.userService.user, period);
			result[`${period[0]}-${period[1]}`] = sequenceId;
		});

		return result;
	}

	private removeFuturePeriods(periods: TimecardPeriod[]): TimecardPeriod[] {
		const today: Moment = moment();
		return filter(periods, (period: TimecardPeriod) => {
			return today.isAfter(moment(period, VIEW_DATE_FORMAT));
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/reports/period-selector/period-selector.component.ts